import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Fade,
  Divider,
  Alert
} from '@mui/material';
import Replay from '@mui/icons-material/Replay';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useFileDownload } from '../Hooks/useFileDownload';
import { useFileApi } from '../Hooks/useFileApi';
import { useNotification } from '../common/NotificationContext';
import { DownloadProgressSection, FileDownloadList } from './DownloadPageComponents';
import './DownloadPageComponent.css';

const DownloadPageComponent = ({ activeTheme }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [downloadStatus, setDownloadStatus] = useState('preparing');
  const [fileStatuses, setFileStatuses] = useState({});
  const [errorCount, setErrorCount] = useState(0);

  const sessionToken = searchParams.get('sessionToken');
  const filename = searchParams.get('filename');

  const { downloading, isZipping, progress, downloadByFilenames } = useFileDownload(null);
  const { resolveDownloadToken } = useFileApi();
  const notify = useNotification();

  const { data: files = [], isError: hasFetchError } = useQuery({
    queryKey: ['download-files', sessionToken, filename],
    queryFn: async () => {
      if (sessionToken) {
        const fetchedFiles = await resolveDownloadToken(sessionToken);

        if (!Array.isArray(fetchedFiles)) {
          throw new Error('Malformed response: expected files array');
        }

        return fetchedFiles.map(f => f.path || f);
      } else if (filename) {
        return [decodeURIComponent(filename)];
      }
      throw new Error('No download parameters provided');
    },
    enabled: !!sessionToken || !!filename,
    retry: 1,
    staleTime: 1000 * 60 * 15,
  });

  useEffect(() => {
    if (files.length > 0 && Object.keys(fileStatuses).length === 0) {
      const initialStatuses = {};
      files.forEach((_, index) => {
        initialStatuses[index] = 'pending';
      });
      setFileStatuses(initialStatuses);
    }
  }, [files, fileStatuses]);

  useEffect(() => {
    if (hasFetchError) {
      setDownloadStatus('error');
    }
  }, [hasFetchError]);

  const handleFileStatusChange = useCallback((index, status) => {
    setFileStatuses(prev => ({ ...prev, [index]: status }));
    if (status === 'error') {
      setErrorCount(prev => prev + 1);
    }
  }, []);

  const startDownload = useCallback(async (filesToDownload) => {
    setDownloadStatus('downloading');
    setErrorCount(0);

    try {
      await downloadByFilenames(filesToDownload, handleFileStatusChange);
      setDownloadStatus('completed');
    } catch (error) {
      console.error('Download process finished with errors:', error);
      setDownloadStatus('partial_failure');
    }
  }, [downloadByFilenames, handleFileStatusChange]);

  useEffect(() => {
    if (files.length > 0 && downloadStatus === 'preparing' && Object.keys(fileStatuses).length > 0) {
      startDownload(files);
    }
  }, [files, downloadStatus, startDownload, fileStatuses]);

  useEffect(() => {
    if (downloadStatus === 'completed') {
      notify({ message: 'All files downloaded successfully!', severity: 'success' });
    } else if (downloadStatus === 'partial_failure') {
      notify({ message: `${errorCount} ${errorCount === 1 ? 'file' : 'files'} could not be downloaded.`, severity: 'warning' });
    } else if (downloadStatus === 'error') {
      notify({ message: 'A critical error occurred. Please try again.', severity: 'error' });
    }
  }, [downloadStatus, errorCount, notify]);

  const handleRetry = () => {
    const failedItems = files.map((file, index) => ({ file, index }))
      .filter(item => fileStatuses[item.index] === 'error');

    const newStatuses = { ...fileStatuses };
    failedItems.forEach(item => {
      newStatuses[item.index] = 'pending';
    });
    setFileStatuses(newStatuses);

    const retryStatusChangeWrapper = (subsetIndex, status) => {
      const originalIndex = failedItems[subsetIndex].index;
      handleFileStatusChange(originalIndex, status);
    };

    setDownloadStatus('downloading');
    setErrorCount(0);
    
    downloadByFilenames(failedItems.map(item => item.file), retryStatusChangeWrapper)
      .then(() => setDownloadStatus('completed'))
      .catch((error) => {
        console.error('Download retry process finished with errors:', error);
        setDownloadStatus('partial_failure');
      });
  };

  const handleClose = () => {
    window.close();
    setTimeout(() => {
      if (!document.hidden) {
        navigate('/files');
      }
    }, 100);
  };

  const completedCount = Object.values(fileStatuses).filter(s => s === 'completed').length;
  const settledCount = completedCount + errorCount;
  const overallProgress = files.length > 0 ? (settledCount / files.length) * 100 : 0;
  const currentProgress = downloading ? progress : overallProgress;
  const isDark = activeTheme?.palette?.mode === 'dark';

  return (
    <ThemeProvider theme={activeTheme || createTheme()}>
      <Box 
        className="download-page"
        sx={{
          bgcolor: 'background.default',
          minHeight: '100vh',
          padding: 2,
          paddingTop: 3
        }}
      >
        <Fade in={true} timeout={500}>
          <Card 
            className="download-card" 
            elevation={0}
            sx={{
              maxWidth: 900,
              width: '100%',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CardContent className="download-card-content">
              {/* Header Section */}
              <Box className="download-header" sx={{ marginBottom: 2 }}>
                <Typography 
                  variant="h5" 
                  component="h1" 
                  className="download-title"
                  sx={{ fontWeight: 500, marginBottom: 1 }}
                >
                  File Download
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  className="download-subtitle"
                >
                  {files.length} {files.length === 1 ? 'file' : 'files'} ready to download
                </Typography>
              </Box>

              <Divider sx={{ marginY: 2 }} />

              <Alert severity="info" sx={{ marginBottom: 2 }}>
                If you experience issues with downloads, make sure you are connected to GM networking or VPN, or try using Microsoft Edge browser.
              </Alert>

              {/* Action Buttons */}
              {(downloadStatus === 'completed' || downloadStatus === 'partial_failure' || downloadStatus === 'error') && (
                <Fade in={true} timeout={400}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 2 }}>
                    {downloadStatus === 'partial_failure' && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Replay />}
                        onClick={handleRetry}
                        sx={{ borderColor: 'divider', textTransform: 'none' }}
                      >
                        Retry Failed
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleClose}
                      sx={{ borderColor: 'divider', textTransform: 'none' }}
                    >
                      {downloadStatus === 'completed' ? 'Done' : 'Close'}
                    </Button>
                  </Box>
                </Fade>
              )}

              {/* Progress Section */}
              <DownloadProgressSection 
                currentProgress={currentProgress}
                settledCount={settledCount}
                totalFiles={files.length}
                completedCount={completedCount}
                errorCount={errorCount}
                activeTheme={activeTheme}
                isDark={isDark}
              />

            {/* File List Section */}
            <FileDownloadList 
              files={files}
              fileStatuses={fileStatuses}
              downloadStatus={downloadStatus}
            />

          </CardContent>
        </Card>
      </Fade>
    </Box>
    </ThemeProvider>
  );
};

export default DownloadPageComponent;